import { ClientPerspective, createClient, type QueryParams } from 'next-sanity';
import { SanityConfig } from '@/libs/sanity/config';
import imageUrlBuilder from '@sanity/image-url';
import { SanityImageSource } from '@sanity/image-url/lib/types/types';

export const client = createClient(SanityConfig);

/**
 * Fetches data in Server Components with support for Draft Mode and perspectives.
 * Uses time-based revalidation for "published" perspective (60 seconds).
 * Fetches live API data for "previewDrafts" perspective without caching.
 */
export async function sanityFetch<QueryResponse>({
  query,
  params = {},
  perspective = 'published',
}: {
  query: string;
  params?: QueryParams;
  perspective?: ClientPerspective;
  stega?: boolean;
}) {
  const options = {
    perspective,
    useCdn: perspective === 'published',
    next: {
      revalidate: perspective === 'published' ? 60 : 0,
      tags: ['sanity'],
    },
  };

  return client.fetch<QueryResponse>(query, params, options);
}

export function getSanityImageUrl(source: SanityImageSource) {
  return imageUrlBuilder(client).image(source).auto('format').fit('max').url();
}

async function fetchDataBySlug(type: string, slug: string) {
  return await sanityFetch<any>({
    query: `*[_type == '${type}' && metadata.slug.current == '${slug}'][0]{
      ...,
      "relatedNewsAndEvents": *[_type=='newsAndEvent' && references(^._id)]{...} | order(publishDate desc)[0..2]
    }`,
  });
}

export const getDataBySlug = (slug: string) => fetchDataBySlug('page', slug);
export const getRealEstatePageDataBySlug = (slug: string) =>
  fetchDataBySlug('realEstate', slug);
export const getDigitalWorkplacePageDataBySlug = (slug: string) =>
  fetchDataBySlug('digitalWorkplace', slug);
export const getUtilitiesPageDataBySlug = (slug: string) =>
  fetchDataBySlug('utilities', slug);
export const getIotPageDataBySlug = (slug: string) =>
  fetchDataBySlug('iot', slug);

export async function getSectionDataByName(name: string) {
  return await sanityFetch<any>({
    query: `*[_type == 'section' && name == '${name}'][0] {
      ...,
      "modules": modules[] {
        ...,
        "slideItems": slideItems[] {
          ...,
          "link": link{
            ...,
            "internal": internal {
              ...,
              "ref": *[(_type == 'page' || _type == 'realEstate' || _type == 'digitalWorkplace' || _type == 'utilities' || _type == 'iot') 
                && _id == ^._ref][0]
            }
          }
        },
        "modules": modules[] {
          ...,
          "items": items[] {
            ...,
            "link": link {
              ...,
              "internal": internal {
                ...,
                "ref": *[(_type == 'page' || _type == 'realEstate' || _type == 'digitalWorkplace' || _type == 'utilities' || _type == 'iot') 
                    && _id == ^._ref][0]
              }
            }
          },
          "link": link {
            ...,
            "internal": internal {
              ...,
              "ref": *[(_type == 'page' || _type == 'realEstate' || _type == 'digitalWorkplace' || _type == 'utilities' || _type == 'iot') 
                        && _id == ^._ref][0]{
                ...,
                
              }
            }
          }
        }
      },
    }`,
  });
}

export async function getPageById(id: string) {
  return await sanityFetch<any>({
    query: `*[(_type == 'page' || _type == 'realEstate' || _type == 'digitalWorkplace' || _type == 'utilities' || _type == 'iot') && _id == '${id}'][0]`,
  });
}

export async function getNewsAndEventsPageDataBySlug(slug: string) {
  return await sanityFetch<any>({
    query: `*[_type == 'newsAndEvent' && metadata.slug.current == '${slug}'][0]`,
  });
}

export function isHomePage(slug: string) {
  return !slug || slug === '/';
}

export const getPartnersPageDataBySlug = (slug: string) =>
  fetchDataBySlug('partners', slug);
